import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import 'react-chat-elements/dist/main.css';
import { Box, LinearProgress } from '@mui/material';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import '@stream-io/video-react-sdk/dist/css/styles.css';
import PropTypes from 'prop-types';
import UserContext from '../contexts/UserContext';
import { db } from '../firebaseConfig';
import ConversationsList from '../components/chats-page/ConversationList';


const ChatsPageLayout = ({ collectionName = "chats", index = false }) => {
    const { user } = useContext(UserContext);
    const [selectedChat, setSelectedChat] = useState(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();

    const [chats, setChats] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const handleClickChat = useCallback((chat) => {
        navigate(`/dashboard/${collectionName}/${chat.id}`, { state: { collectionName, chat } });
    }, [collectionName, navigate]);


    useEffect(() => {
        if (!user) return () => { };
        const unsubscribe = onSnapshot(
            query(collection(db, collectionName), where("members", "array-contains", user.id)),
            (snapshot) => {
                console.log("Chat update ")
                const chats = [];
                snapshot.forEach((doc) => {
                    chats.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
                const sortedChats = chats.sort((a, b) => b.lastUpdate - a.lastUpdate);
                setChats(sortedChats);

                if (index && chats.length > 0) {
                    handleClickChat(chats[0]);
                }

                setIsLoading(false);
            }
        );

        return () => unsubscribe();
    }, [collectionName, handleClickChat, index, user]);

    useEffect(() => {
        if (chats.length === 0 || !id) return;

        const chat = chats.find(chat => chat.id === id);
        if (chat) {
            setSelectedChat(chat);
        }
    }, [id, chats]);

    return (
        <>
            <Helmet>
                <title>{t(collectionName)}</title>
            </Helmet>
            {
                isLoading
                    ? <LinearProgress />
                    : !chats.length ? (
                        <Box height="100%" display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap="2rem">
                            <div style={{ fontSize: '2rem' }}>{t('No chat sessions')}</div>
                        </Box>
                    ) : (
                        <div style={styles.container} className='spacing-x-4'>

                            <div style={styles.flex}>
                                <div style={styles.conversationList}>
                                    <ConversationsList chats={chats} onClickChat={handleClickChat} selectedChat={selectedChat} />
                                </div>
                                <div size={8} style={styles.messages}>
                                    {
                                        index
                                            ? "Loading..."
                                            : <Outlet />
                                    }
                                </div>
                            </div>
                        </div>
                    )
            }
        </>
    );
}
ChatsPageLayout.propTypes = {
    collectionName: PropTypes.string,
    index: PropTypes.bool
}



const styles = {
    container: {
        height: '100%',
        position: 'relative'
    },
    flex: {
        height: 'calc(100vh - 116px)',
        width: '100%',
        position: 'absolute',
        display: 'flex',
    },
    messages: {
        position: 'relative',
        scrollbarWidth: 'none',
        paddingBottom: '2rem',
        height: '100%',
        flex: 2
    },
    conversationList: {
        position: 'relative',
        height: '100%',
        overflow: 'auto',
        flex: 1
    }
}

export default ChatsPageLayout;